import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NavLink as NavLinkBase, useHistory, useLocation } from "react-router-dom";
import { IncreaseRequestCount } from "@modules/global";
import { initParams } from "@modules/channels";
import { closeModal } from "@modules/createAssetsBulk";
import compareVersions, {
    VERSION_CM_11209_ADD_CUSTOM_METADATA,
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_15774_PROMPT_PRESETS,
    VERSION_CM_4601_MUSIC_CONTENTS,
    VERSION_CM_4601_PHOTO_CONTENTS,
    VERSION_CM_5112_PROJECT_SITE_ACCESS,
    VERSION_CM_8075_SHARE_REQUEST,
    VERSION_CM_8558_METADATA_TEMPLATE,
    VERSION_CM_8998_ASSET_ARCHIVE,
    VERSION_CM_9766_COLLECTIONS,
    VERSION_CM_16961_AUTOMATED_WORKFLOW,
    VERSION_DAM_372_WEBHOOK,
    // [DAM-466] AI Analysis Plan and Usage 메뉴 임시 비노출로 미사용 — 재노출 시 주석 해제
    // VERSION_DAM_135_AI_BILLING,
} from "@cores/version";
import equal from "deep-equal";
import { closeNavigator, openMenu, openNavigator, setMenu, toggleMenu } from "@modules/navigator";
import {
    IconAiBuilder,
    IconArrowBack,
    IconArrowCircleRight,
    IconArrowForward,
    IconAssets,
    IconAutomation,
    IconBack,
    IconCategory,
    IconChevronDown,
    IconChevronUp,
    IconCircle,
    IconContents,
    IconDashboard,
    IconEventtriggers,
    IconGroup,
    IconJobs,
    IconWebhook,
    IconLive,
    IconMetadata,
    // [DAM-466] AI Analysis Plan and Usage 메뉴 임시 비노출로 미사용 — 재노출 시 주석 해제
    // IconPieChart,
    IconSchema,
    IconSettingAdmin,
    IconSettingProject,
    IconShared,
    IconStorage,
    IconUser,
} from "@mzc-cloudplex/icons";

import { isDev, isLocal, isQA, isStage } from "@cores/enviromentExtension";
import SelectProject from "./components/SelectProject";
import menuService from "@services/menuService";
import {
    Box,
    Collapse,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListSubheader,
    Tooltip,
    Typography,
} from "@mzc-pdc/ui";
import { BackToConsoleButton, Drawer, ProjectSettingButton } from "./nav.styled";
import { EllipsisListItemText } from "@components_v2/ellipsis-tooltip";
import theme from "@styles/theme";
import { alpha, useMediaQuery } from "@mui/material";
import { getContentSchemas } from "@modules/navigator/navigator";
import { NavigatorMenuSkeleton, NavigatorSubMenuSkeleton } from "@components/navigator/navigator-skeleton";
import { useInView } from "react-intersection-observer";
import useSettings from "@hooks/useSettings";
import { MENU_LIST } from "@constants";
import { siteMode } from "@cores/siteMode";

const NavLink = React.forwardRef((props, ref) => (
    <NavLinkBase
        ref={ref}
        {...props}
        className={({ isActive }) => `${props.className} ${isActive ? props.activeClassName : ""}`}
    />
));
NavLink.displayName = "NavLink";

const Navigator = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();
    const { t } = useTranslation();
    const { navigatorMenus } = useSettings({ type: "General" });
    const { getNavigatorMenuName, navigatorMenusName } = useSettings({ type: "MenuName" });
    const { domainStatus } = useSettings({ type: "DomainStatus" });
    const { getSettingData, resourceSettings } = useSettings({ type: "AI" });
    const LIMIT = 20;
    const isContentSchemaIdChecked = useRef();
    const isCms = siteMode.get() === "CMS";

    const [menuType, setMenuType] = useState("console");
    const { spaceId, projectId } = menuService.parse(location.pathname);
    const [activeStage, setActiveStage] = useState(null);
    const [activeProject, setActiveProject] = useState(null);
    const [navOpen, setNavOpen] = useState(true);
    const [nextToken, setNextToken] = useState(null);

    const [targetRef, inView] = useInView();

    const global = useSelector(({ global }) => global);
    const user = useSelector(({ user }) => user);
    const project = useSelector(({ project }) => project);
    const menus = useSelector(({ navigator }) => navigator.menus);
    const projectMenus = useSelector(({ navigator }) => navigator.projectMenus);
    const active = useSelector(({ navigator }) => navigator.active);
    const stage = useSelector(({ stage }) => stage);
    const contentSchemas = useSelector(({ navigator }) => navigator.contents);

    const currentMenu = menus.find((item) => location.pathname.indexOf(item.path) !== -1);
    const [selectedMenu, setSelectedMenu] = useState(currentMenu?.id);

    const upMatches = useMediaQuery(theme.breakpoints.up("xl"));

    const loadContentSchemas = async (token) => {
        try {
            if (contentSchemas.pending) return;

            const params = {
                limit: LIMIT,
                status: "ACTIVE",
                nextToken: token,
            };
            if (compareVersions(stage.version, VERSION_CM_11209_ADD_CUSTOM_METADATA) >= 0) params.type = "CONTENTS";
            const nextToken = await dispatch(getContentSchemas({ projectId: project.id, params }));

            setNextToken(nextToken);
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        if (domainStatus.CUSTOM_CONTENTS_OFF) return;
        if (inView) {
            loadContentSchemas(nextToken);
        }
    }, [inView, targetRef]);

    useEffect(() => {
        if (user && user.stages.data) {
            const activeStage = user.stages.data.filter((v) => v.id === stage.id)[0];
            if (activeStage) {
                const activeProject = activeStage.projects
                    ? activeStage.projects.filter((v) => v.id === project.id)[0]
                    : null;
                setActiveStage(activeStage);
                setActiveProject(activeProject);
            }
        }

        bindMenus();
    }, []);

    useEffect(() => {
        bindMenus();
    }, [navigatorMenus, stage?.version, resourceSettings]);

    useEffect(() => {
        if (user && user.stages.data) {
            const activeStage = user.stages.data.filter((v) => v.id === stage.id)[0];
            if (activeStage) {
                const activeProject = activeStage.projects
                    ? activeStage.projects.filter((v) => v.id === project.id)[0]
                    : null;

                setActiveStage(activeStage);
                setActiveProject(activeProject);
                // setShowStageLayer(false);
            }

            if (menuType !== "admin" && stage?.version && !domainStatus.CUSTOM_CONTENTS_OFF) {
                loadContentSchemas();
                setNextToken(null);
            }
        }
    }, [user, project]);

    useEffect(() => {
        const newMenuType = location.pathname.split("/").filter((s) => s !== "")[0]; //ADMIN 페이지 여부

        if (!equal(menuType, newMenuType)) {
            if (menuType === "admin" && newMenuType !== "admin") {
                leave(); //Note: 콘솔->셋팅 이동 후 버튼 사용해 콘솔로 돌아가는 것과 브라우저 뒤로가기를 한 경우의 동작이 달라서 일관된 동작을 위해 추가했습니다.
            } else {
                setMenuType(newMenuType);
                dispatch(toggleMenu(menuType));
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        setSelectedMenu(currentMenu?.id);
    }, [location]);

    useEffect(() => {
        if (!project?.id) return;

        getSettingData("AI");
    }, [project?.id, getSettingData]);

    const bindMenus = () => {
        const hideLive = stage && stage.extensions && stage.extensions["HIDE_LIVE"] === "true";
        const hideContents = stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";

        const firstMenu = location.pathname.split("/")[1];
        if (!firstMenu) {
            history.push("/");
        }
        const projectMenus = buildProjectMenus();
        dispatch(
            setMenu({
                projectMenus,
                active: true,
            }),
        );

        let menus = buildMenus({ hideLive, hideContents });
        if (isLocal || isDev || isStage || isQA) {
            menus = menus.concat(buildDemoMenus());
        }
        if (!isCms && !hideContents) {
            menus.push({
                id: "JOBS",
                name: getNavigatorMenuName("Jobs"),
                icon: <IconJobs size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-jobs",
                path: menuService.paths.jobs(spaceId, projectId),
                active: false,
                isOpen: true,
                visible: navigatorMenus?.[MENU_LIST.JOBS]?.visible,
            });
        }
        dispatch(
            setMenu({
                menus: menus.map((v) => {
                    v.active = firstMenu ? v.path.indexOf(firstMenu) !== -1 : v.id === "transcoding";
                    return v;
                }),
                active: true,
            }),
        );
    };
    const getMenuPathByDomain = (spaceId, projectId, domain) => {
        if (isCms) return menuService.paths.library[domain]?.(spaceId, projectId);
        else return menuService.paths[domain]?.(spaceId, projectId);
    };

    const buildMenus = ({ hideLive, hideContents }) => {
        // const { location, stage, t } = this.props;
        const stageVersion = stage.version;
        const { spaceId, projectId } = menuService.parse(location.pathname);

        const libraryHiddenItems = isCms ? ["JOBS", "LIVE", "SHARED", "CONTENTS", "ASSETS.ARCHIVED"] : [];

        const isHiddenInLibrary = (menuId, submenuId = null) => {
            if (submenuId) {
                return libraryHiddenItems.includes(`${menuId}.${submenuId}`);
            }
            return libraryHiddenItems.includes(menuId);
        };

        const calculateMenuVisible = (menuId, menuListId, submenuId = null) => {
            if (navigatorMenus?.[menuListId]?.visible === false) {
                return false;
            }

            if (isHiddenInLibrary(menuId)) {
                return false;
            }
            if (submenuId && isHiddenInLibrary(menuId, submenuId)) {
                return false;
            }

            return true;
        };

        const menus = [];

        menus.push({
            id: "DASHBOARD",
            name: getNavigatorMenuName("Dashboard"),
            path: getMenuPathByDomain(spaceId, projectId, "dashboard"),
            icon: <IconDashboard size={16} htmlColor={theme.palette.greySecondary.main} />,
            iconClass: "sprite sprite-menu-dashboard",
            visible: calculateMenuVisible("DASHBOARD", MENU_LIST.DASHBOARD),
        });

        menus.push({
            id: "STORAGE_MENUS",
            name: getNavigatorMenuName("Storage"),
            path: getMenuPathByDomain(spaceId, projectId, "storage"),
            icon: <IconStorage size={16} htmlColor={theme.palette.greySecondary.main} />,
            iconClass: "sprite sprite-menu-storage",
            isOpen: true,
            visible: calculateMenuVisible("STORAGE", MENU_LIST.STORAGES),
        });

        const assetMenu_v2 = {
            id: "ASSETS_MENU",
            name: getNavigatorMenuName("Assets"),
            icon: <IconAssets size={16} htmlColor={theme.palette.greySecondary.main} />,
            iconClass: "sprite sprite-menu-assets",
            path: getMenuPathByDomain(spaceId, projectId, "assetsAssets"),
            isOpen: true,
            visible: calculateMenuVisible("ASSETS", MENU_LIST.ASSETS),
            subMenus: [
                {
                    id: "ASSETS",
                    name: t(`common::label::${"Assets"}`),
                    path: getMenuPathByDomain(spaceId, projectId, "assetsAssets"),
                    visible: calculateMenuVisible("ASSETS", MENU_LIST.ASSETS, "ASSETS"),
                },
                {
                    id: "COLLECTIONS",
                    name: t(`common::label::${"Collections"}`, "Collections"),
                    path: getMenuPathByDomain(spaceId, projectId, "assetsCollections"),
                    disabled: !(compareVersions(VERSION_CM_9766_COLLECTIONS, stageVersion) <= 0),
                    visible: calculateMenuVisible("ASSETS", MENU_LIST.COLLECTIONS, "COLLECTIONS"),
                },
                {
                    id: "ARCHIVED",
                    name: t(`common::label::${"Archived"}`),
                    path: getMenuPathByDomain(spaceId, projectId, "archiveAssets"),
                    disabled: !(compareVersions(VERSION_CM_8998_ASSET_ARCHIVE, stageVersion) <= 0),
                    visible: calculateMenuVisible("ASSETS", MENU_LIST.ARCHIVED, "ARCHIVED"),
                },
            ],
        };
        menus.push(assetMenu_v2);

        if (compareVersions(VERSION_CM_13766_SHARES_V3, stageVersion) > 0) {
            const sharedSubmenus = [
                {
                    id: "SHARED_HISTORIES",
                    name: t(`common::label::${"Shared Histories"}`),
                    path: menuService.paths.sharedHistory(spaceId, projectId),
                    disabled: !(compareVersions(VERSION_CM_8075_SHARE_REQUEST, stageVersion) <= 0),
                    visible: calculateMenuVisible("SHARED", MENU_LIST.SHARED_HISTORIES, "SHARED_HISTORIES"),
                },
                {
                    id: "SHARED_BOX",
                    name: t(`common::label::${"Shared Box"}`),
                    path: menuService.paths.sharedBox(spaceId, projectId),
                    disabled: !(compareVersions(VERSION_CM_8075_SHARE_REQUEST, stage.version) <= 0),
                    visible: calculateMenuVisible("SHARED", MENU_LIST.SHARED_BOX, "SHARED_BOX"),
                },
            ];

            menus.push({
                id: "SHARED",
                name: getNavigatorMenuName("Shared"),
                path: menuService.paths.shared(spaceId, projectId),
                icon: <IconShared size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-shared",
                isOpen: true,
                visible: calculateMenuVisible("SHARED", MENU_LIST.SHARED_HISTORIES, "SHARED_HISTORIES"),
                subMenus: sharedSubmenus,
            });
        }
        if (!hideContents) {
            const customContents = domainStatus.CUSTOM_CONTENTS_OFF
                ? []
                : contentSchemas.data?.map((el) => ({
                      id: el.id,
                      name: el.name,
                      path: menuService.paths.contentsCustomContents(spaceId, projectId, el.id),
                      visible: calculateMenuVisible("CONTENTS", MENU_LIST.CUSTOM_CONTENTS, `CUSTOM_${el.id}`),
                  })) || [];

            const contentsSubmenus = [
                {
                    id: "VIDEOS",
                    name: t(`common::label::${"Videos"}`),
                    path: menuService.paths.contentsVideos(spaceId, projectId),
                    visible: calculateMenuVisible("CONTENTS", "VIDEOS", MENU_LIST.VIDEOS),
                },
                {
                    id: "PEOPLE",
                    name: t(`common::label::${"People"}`),
                    path: menuService.paths.contentsPeople(spaceId, projectId),
                    visible: calculateMenuVisible("CONTENTS", "PEOPLE", MENU_LIST.PEOPLE),
                },
                {
                    id: "MUSIC",
                    name: t(`common::label::${"Music"}`),
                    path: menuService.paths.contentsMusic(spaceId, projectId),
                    disabled: !(compareVersions(VERSION_CM_4601_MUSIC_CONTENTS, stageVersion) <= 0),
                    visible: calculateMenuVisible("CONTENTS", "MUSIC", MENU_LIST.MUSIC),
                },
                {
                    id: "PHOTO",
                    name: t(`common::label::${"Photo"}`),
                    path: menuService.paths.contentsPhoto(spaceId, projectId),
                    disabled: !(compareVersions(VERSION_CM_4601_PHOTO_CONTENTS, stageVersion) <= 0),
                    visible: calculateMenuVisible("CONTENTS", "PHOTO", MENU_LIST.PHOTO),
                },
                ...customContents,
            ];

            menus.push({
                id: "CONTENTS_MENUS",
                name: getNavigatorMenuName("Contents"),
                icon: <IconContents size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-media",
                path: menuService.paths.contentsVideos(spaceId, projectId),
                isOpen: true,
                visible: calculateMenuVisible("CONTENTS", MENU_LIST.VIDEOS),
                subMenus: contentsSubmenus,
            });
        }
        if (!hideLive) {
            const liveSubmenus = [
                {
                    id: "STREAMS",
                    name: t(`common::label::${"Streams"}`),
                    path: menuService.paths.liveStream(spaceId, projectId),
                    visible: calculateMenuVisible("LIVE", MENU_LIST.STREAMS),
                },
                {
                    id: "SCHEDULES",
                    name: t(`common::label::${"Schedules"}`),
                    path: menuService.paths.liveSchedules(spaceId, projectId),
                    visible: calculateMenuVisible("LIVE", MENU_LIST.SCHEDULES),
                },
                {
                    id: "CHANNELS",
                    name: t(`common::label::${"Channels"}`),
                    path: menuService.paths.channels(spaceId, projectId),
                    visible: calculateMenuVisible("LIVE", MENU_LIST.CHANNELS),
                },
            ];

            menus.push({
                id: "LIVE",
                name: getNavigatorMenuName("Live"),
                icon: <IconLive size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-live",
                path: menuService.paths.liveStream(spaceId, projectId),
                isOpen: true,
                visible: calculateMenuVisible("LIVE", MENU_LIST.STREAMS),
                subMenus: liveSubmenus,
            });
        }
        return menus;
    };

    // TODO : 콘솔 구현 후, 정합
    const buildDemoMenus = () => {
        // const { t, stage } = this.props;
        return [
            // {
            //     id: "chat",
            //     name: "Chat",
            //     iconClass: "sprite sprite-menu-chat",
            //     path: "/chat",
            //     active: false,
            //     disabled: false,
            // },
        ];
    };

    const buildProjectMenus = () => {
        // const { t, stage } = this.props;
        // const { spaceId, projectId } = this.state;
        const menus = [
            {
                id: "Access",
                name: t(`common::label::${"Access"}`),
                active: false,
                isOpen: true,
                subMenus: [
                    {
                        id: "authorities",
                        name: t(`common::label::${"Users"}`),
                        path: menuService.paths.admin.authoritiesUser(spaceId, projectId),
                        icon: <IconUser size={16} htmlColor={theme.palette.greySecondary.main} />,
                        iconClass: "sprite sprite-menu-user",
                        disabled: false,
                        active: true,
                    },
                    {
                        id: "authorities",
                        name: t(`common::label::${"Groups"}`),
                        icon: <IconGroup size={16} htmlColor={theme.palette.greySecondary.main} />,
                        path: menuService.paths.admin.authoritiesGroup(spaceId, projectId),
                        iconClass: "sprite sprite-menu-team",
                        disabled: false,
                        active: true,
                    },
                ],
            },
            {
                id: "Manage",
                name: t(`common::label::${"Manage"}`),
                active: false,
                isOpen: true,
                subMenus: [
                    {
                        id: "category",
                        name: t(`common::label::${"Categories"}`),
                        path: menuService.paths.admin.categoryGroups(spaceId, projectId),
                        icon: <IconCategory size={16} htmlColor={theme.palette.greySecondary.main} />,
                        iconClass: "sprite sprite-menu-category",
                        disabled: false,
                        active: true,
                    },
                    {
                        id: "eventTriggers",
                        name: t(`common::label::${"Event triggers"}`),
                        path: menuService.paths.admin.eventTriggers(spaceId, projectId),
                        icon: <IconEventtriggers size={16} htmlColor={theme.palette.greySecondary.main} />,
                        iconClass: "sprite sprite-menu-eventtrigger",
                        disabled: false,
                        active: true,
                    },
                ],
            },
            {
                id: "settings",
                name: t(`common::label::${"Settings"}`),
                path: menuService.paths.admin.projectSettings(spaceId, projectId),
                icon: <IconSettingAdmin size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-setting",
                disabled: false,
                active: true,
            },
        ];

        const manageSubMenu = menus.find((v) => v.id === "Manage")?.subMenus;

        if (compareVersions(stage.version, VERSION_CM_16961_AUTOMATED_WORKFLOW) >= 0) {
            manageSubMenu.push({
                id: "Workflow",
                name: t(`common::label::Workflow`),
                path: menuService.paths.admin.automatedWorkflow(spaceId, projectId),
                icon: <IconAutomation size={16} />,
                iconClass: "sprite sprite-menu-setting-inverse",
                active: true,
            });
        }

        // [DAM-438] 웹훅은 워크플로우 다음 순서로 노출한다(인계 문서 DAM-439 §2.1 — LNB 위치 확정).
        if (compareVersions(stage.version, VERSION_DAM_372_WEBHOOK) >= 0) {
            manageSubMenu.push({
                id: "webhooks",
                name: t(`common::label::Webhooks`),
                path: menuService.paths.admin.webhooks(spaceId, projectId),
                icon: <IconWebhook size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-setting-inverse",
                disabled: false,
                active: true,
            });
        }

        if (compareVersions(stage.version, VERSION_CM_8558_METADATA_TEMPLATE) >= 0) {
            manageSubMenu.push({
                id: "metadataTemplate",
                name: t(`common::label::${"Metadata Template"}`),
                path: menuService.paths.admin.metadataTemplate(spaceId, projectId),
                icon: <IconMetadata size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-eventtrigger",
                disabled: false,
                active: true,
            });
        }

        if (!domainStatus.CUSTOM_CONTENTS_OFF) {
            manageSubMenu.push({
                id: "contentSchemas",
                name: t(`common::label::${"Content Schemas"}`),
                path: menuService.paths.admin.contentSchemas(spaceId, projectId),
                icon: <IconSchema size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-metadata",
                disabled: false,
                active: true,
            });
        }
        if (
            compareVersions(stage.version, VERSION_CM_15774_PROMPT_PRESETS) >= 0 &&
            stage?.extensions?.["AI_WORKSPACE_ENABLED"] === "true" &&
            resourceSettings?.settings?.AI?.useAIWorkspace?.value === "true"
        ) {
            manageSubMenu.push({
                id: "promptPresets",
                name: t(`common::label::AI Workspace`),
                path: menuService.paths.admin.promptPresets(spaceId, projectId),
                icon: <IconAiBuilder size={16} />,
                iconClass: "sprite sprite-menu-setting-inverse",
                active: true,
            });
        }
        // [DAM-466] AI Analysis Plan and Usage 메뉴를 임시로 노출하지 않는다. 재노출 시 아래 블록과 관련 import 주석을 해제한다.
        // 라우트(/ai-billing)·페이지·redux 모듈은 그대로 유지하므로 URL 직접 진입은 동작한다.
        // AI Workspace 가 켜진 프로젝트에서만 노출한다.
        // if (
        //     compareVersions(stage.version, VERSION_DAM_135_AI_BILLING) >= 0 &&
        //     resourceSettings?.settings?.AI?.useAIWorkspace?.value === "true"
        // ) {
        //     manageSubMenu.push({
        //         id: "aiBilling",
        //         name: t(`common::label::AI Analysis Plan and Usage`),
        //         path: menuService.paths.admin.aiBilling(spaceId, projectId),
        //         icon: <IconPieChart size={16} />,
        //         iconClass: "sprite sprite-menu-setting-inverse",
        //         active: true,
        //     });
        // }
        return menus;
    };

    const resizingHandler = () => {
        if (window.innerWidth <= 1650) {
            dispatch(closeNavigator());
        } else {
            dispatch(openNavigator());
        }
    };

    const handleNavToggle = () => {
        setNavOpen(!navOpen);
    };

    const getNavToggleIcon = () => {
        if (navOpen) return <IconArrowBack size={12} htmlColor={`#fff`} />;
        return <IconArrowForward size={12} htmlColor={`#fff`} />;
    };

    const leave = () => {
        history.replace("/");
    };

    const TransIconComponent = useCallback((props) => {
        const Icon = props.icon;
        return <Icon {...props} />;
    }, []);

    const navMenus = useMemo(() => {
        return menus.map((item) => {
            item.hasSubmenu = item?.subMenus?.length > 0;
            return item;
        });
    }, [menus]);

    useEffect(() => {
        setNavOpen(upMatches);
    }, [upMatches]);

    useEffect(() => {
        if (!contentSchemas.data) return;
        bindMenus();
    }, [contentSchemas.data]);

    return (
        <>
            <Drawer
                variant="permanent"
                open={navOpen}
                sx={{
                    ".MuiDrawer-paper": {
                        top: "56px",
                        height: `calc(100% - 56px )`,
                        overflowY: "hidden",
                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    },
                }}
            >
                {/*<Toolbar />*/}

                <SelectProject open={navOpen} />
                {menuType === "admin" && (
                    <>
                        {navOpen ? (
                            <Box
                                sx={{
                                    margin: `16px 24px`,
                                }}
                            >
                                <Typography
                                    variant={"subtitle2_700"}
                                    sx={{
                                        // 고정 height 대신 minHeight로 두어, pt-BR 등 긴 라벨이
                                        // 2줄로 줄바꿈될 때 잘리지 않고 늘어나도록 한다.
                                        minHeight: `22px`,
                                        textTransform: `uppercase`,
                                        // Drawer가 whiteSpace: nowrap을 상속시켜 헤더가 줄바꿈되지 않고
                                        // margin을 넘어 흘러넘친다. normal로 상속을 끊어 2줄 줄바꿈되게 한다. (DAM-306)
                                        whiteSpace: `normal`,
                                        wordBreak: `break-word`,
                                    }}
                                >
                                    {t(`common::label::${"Project Settings"}`)}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: `inline-flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                    width: `40px`,
                                    height: `22px`,
                                    margin: `28px 8px 28px 12px`,
                                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                    borderRadius: `4px`,
                                }}
                            >
                                <IconSettingProject size={16} htmlColor={`#fff`} />
                            </Box>
                        )}
                    </>
                )}
                {menuType === "admin" ? (
                    <>
                        <List>
                            {projectMenus &&
                                projectMenus.map((menu, i) => {
                                    let menuActive = location.pathname.includes(menu.path);
                                    return (
                                        <ListItem key={i} divider>
                                            {menu.path ? (
                                                <ListItemButton
                                                    selected={menuActive}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (location.pathname !== menu.path) {
                                                            // console.log('menu.path', menu.path)
                                                            history.push(menu.path);
                                                            dispatch(IncreaseRequestCount());
                                                            dispatch(toggleMenu(menu.id));
                                                            if (menu.path.indexOf("admin") !== -1) {
                                                                dispatch(initParams());
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>{menu.icon && menu.icon}</ListItemIcon>

                                                    <EllipsisListItemText
                                                        primary={menu.name}
                                                        primaryTypographyProps={{
                                                            variant: "body1_500",
                                                            component: "strong",
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ) : (
                                                <>
                                                    <ListSubheader component={"div"}>
                                                        {navOpen ? (
                                                            <Typography
                                                                variant={"body4_700"}
                                                                color={theme.palette.text.secondary}
                                                            >
                                                                {t(`${menu.name}`)}
                                                            </Typography>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: `8px`,
                                                                    height: `2px`,
                                                                    borderRadius: `1px`,
                                                                    margin: `7px auto`,
                                                                    backgroundColor: theme.palette.secondary[600],
                                                                }}
                                                            />
                                                        )}
                                                    </ListSubheader>
                                                    {menu.subMenus && (
                                                        <List>
                                                            {menu.subMenus
                                                                .filter((v) => !v.disabled)
                                                                .map((subMenu, i) => (
                                                                    <ListItem key={i}>
                                                                        {/*<NavLink*/}
                                                                        {/*    to={{*/}
                                                                        {/*        pathname: subMenu.path,*/}
                                                                        {/*        state: "flush",*/}
                                                                        {/*    }}*/}
                                                                        {/*    activeClassName={"active"}*/}
                                                                        {/*    className={`nav-link`}*/}
                                                                        {/*    onClick={() => {*/}
                                                                        {/*        dispatch(openMenu(menu.id));*/}
                                                                        {/*        dispatch(*/}
                                                                        {/*            IncreaseRequestCount(),*/}
                                                                        {/*        );*/}
                                                                        {/*    }}*/}
                                                                        {/*>*/}
                                                                        <ListItemButton
                                                                            component={NavLink}
                                                                            selected={
                                                                                location.pathname === subMenu.path
                                                                            }
                                                                            to={{
                                                                                pathname: subMenu.path,
                                                                                search:
                                                                                    location.pathname === subMenu.path
                                                                                        ? location.search
                                                                                        : "",
                                                                            }}
                                                                            onClick={() => {
                                                                                dispatch(openMenu(menu.id));
                                                                                dispatch(IncreaseRequestCount());
                                                                            }}
                                                                        >
                                                                            <ListItemIcon>
                                                                                {subMenu.icon && subMenu.icon}
                                                                            </ListItemIcon>

                                                                            <EllipsisListItemText
                                                                                primary={subMenu.name}
                                                                                primaryTypographyProps={{
                                                                                    variant: "body1_500",
                                                                                    component: "strong",
                                                                                }}
                                                                            />
                                                                        </ListItemButton>
                                                                        {/*</NavLink>*/}
                                                                    </ListItem>
                                                                ))}
                                                        </List>
                                                    )}
                                                </>
                                            )}
                                        </ListItem>
                                    );
                                })}
                        </List>
                    </>
                ) : (
                    <>
                        {!navigatorMenus || !navigatorMenusName ? (
                            <List sx={{ mt: theme.spacing(1) }}>
                                {new Array(5).fill("").map((_, i) => (
                                    <NavigatorMenuSkeleton key={i} />
                                ))}
                            </List>
                        ) : (
                            <List>
                                {navMenus &&
                                    navMenus.map((menu, i) => {
                                        let menuActive = location.pathname.includes(menu.path);
                                        if (menu.hasSubmenu === true && menuActive === false) {
                                            menuActive = !!menu.subMenus.find((sm) =>
                                                location.pathname.includes(sm.path),
                                            );
                                        }
                                        const menuHidden =
                                            menu.subMenus?.every(
                                                (sub) => sub.visible !== undefined && sub.visible === false,
                                            ) ||
                                            (!menu.hasSubmenu && menu.visible === false);
                                        return (
                                            <Tooltip key={i} title={navOpen ? "" : menu.name} placement={"right"}>
                                                <ListItem hidden={menuHidden}>
                                                    {menu.path ? (
                                                        <>
                                                            {menu.subMenus ? (
                                                                <ListItemButton
                                                                    onClick={(e) => {
                                                                        if (navOpen) dispatch(toggleMenu(menu.id));
                                                                        else {
                                                                            const firstSubMenu = menu.subMenus.filter(
                                                                                (v) => !v.disabled,
                                                                            )?.[0];
                                                                            if (!firstSubMenu) return;

                                                                            if (location.pathname !== menu.path) {
                                                                                dispatch(openMenu(firstSubMenu.id));
                                                                                dispatch(IncreaseRequestCount());
                                                                                history.push(firstSubMenu.path);
                                                                            }
                                                                        }
                                                                    }}
                                                                    selected={menuActive}
                                                                >
                                                                    {menu.icon && (
                                                                        <ListItemIcon>{menu.icon}</ListItemIcon>
                                                                    )}
                                                                    <EllipsisListItemText
                                                                        primary={menu.name}
                                                                        primaryTypographyProps={{
                                                                            variant: "body1_500",
                                                                            component: "strong",
                                                                        }}
                                                                    />
                                                                    {navOpen && (
                                                                        <>
                                                                            {menu.isOpen ? (
                                                                                <IconChevronUp
                                                                                    size={20}
                                                                                    color={
                                                                                        menuActive
                                                                                            ? "primary"
                                                                                            : "secondary"
                                                                                    }
                                                                                    style={{
                                                                                        opacity: 0.48,
                                                                                        transition:
                                                                                            "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <IconChevronDown
                                                                                    size={20}
                                                                                    color={
                                                                                        menuActive
                                                                                            ? "primary"
                                                                                            : "secondary"
                                                                                    }
                                                                                    style={{
                                                                                        opacity: 0.48,
                                                                                        transition:
                                                                                            "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </ListItemButton>
                                                            ) : (
                                                                // <Link
                                                                //
                                                                // >
                                                                <ListItemButton
                                                                    selected={menuActive}
                                                                    // onClick={() => setSelectedMenu(menu.id)}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        setSelectedMenu(menu.id);
                                                                        // 현재 URL의 pathname과 이동하려는 menu.path가 같으면 이동하지 않음
                                                                        if (location.pathname !== menu.path) {
                                                                            history.push(menu.path);
                                                                            dispatch(IncreaseRequestCount());

                                                                            if (menu.path.indexOf("admin") !== -1) {
                                                                                dispatch(initParams());
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {menu.icon && (
                                                                        <ListItemIcon>{menu.icon}</ListItemIcon>
                                                                    )}
                                                                    <EllipsisListItemText
                                                                        primary={menu.name}
                                                                        primaryTypographyProps={{
                                                                            variant: "body1_500",
                                                                            component: "strong",
                                                                        }}
                                                                    />
                                                                </ListItemButton>
                                                                // </Link>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <ListItemButton>
                                                            <EllipsisListItemText
                                                                primary={menu.name}
                                                                primaryTypographyProps={{
                                                                    variant: "body1_500",
                                                                    component: "strong",
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    )}
                                                    {menu.subMenus && navOpen && (
                                                        <Collapse in={menu.isOpen}>
                                                            <List>
                                                                {navOpen && contentSchemas.pending
                                                                    ? new Array(5)
                                                                          .fill("")
                                                                          .map((_, i) => (
                                                                              <NavigatorSubMenuSkeleton key={i} />
                                                                          ))
                                                                    : menu.subMenus
                                                                          .filter(
                                                                              (v) =>
                                                                                  !v.disabled &&
                                                                                  (v.visible === undefined ||
                                                                                      v.visible),
                                                                          )
                                                                          .map((subMenu, i) => (
                                                                              <ListItem key={i}>
                                                                                  <ListItemButton
                                                                                      component={NavLink}
                                                                                      to={{
                                                                                          pathname: subMenu.path,
                                                                                          search:
                                                                                              location.pathname ===
                                                                                              subMenu.path
                                                                                                  ? location.search
                                                                                                  : "",
                                                                                          state: "flush",
                                                                                      }}
                                                                                      onClick={() => {
                                                                                          // console.log(
                                                                                          //     location.pathname ===
                                                                                          //         subMenu.path,
                                                                                          // );
                                                                                          dispatch(openMenu(menu.id));
                                                                                          dispatch(
                                                                                              IncreaseRequestCount(),
                                                                                          );
                                                                                      }}
                                                                                      activeClassName={"Mui-selected"}
                                                                                      sx={{
                                                                                          "&.Mui-selected": {
                                                                                              "& .MuiListItemIcon-root":
                                                                                                  {
                                                                                                      color: `${theme.palette.primary.main} !important`,
                                                                                                  },
                                                                                              "& .MuiTypography-root": {
                                                                                                  color: theme.palette
                                                                                                      .primary.main,
                                                                                              },
                                                                                          },
                                                                                          "&:hover": {
                                                                                              backgroundColor: `${alpha(
                                                                                                  theme.palette
                                                                                                      .primary[100],
                                                                                                  0.72,
                                                                                              )} !important`,
                                                                                              "& .MuiListItemIcon-root":
                                                                                                  {
                                                                                                      color: `${theme.palette.primary.main} !important`,
                                                                                                  },
                                                                                              "& .MuiTypography-root": {
                                                                                                  color: theme.palette
                                                                                                      .primary.main,
                                                                                              },
                                                                                          },
                                                                                      }}
                                                                                  >
                                                                                      <ListItemIcon
                                                                                          sx={{
                                                                                              opacity: `1 !important`,
                                                                                              color: `${theme.palette.greySecondary.light} !important`,
                                                                                          }}
                                                                                      >
                                                                                          <IconCircle size={6} />
                                                                                      </ListItemIcon>
                                                                                      <EllipsisListItemText
                                                                                          primary={subMenu.name}
                                                                                          primaryTypographyProps={{
                                                                                              variant: "body1_400",
                                                                                          }}
                                                                                      />
                                                                                  </ListItemButton>
                                                                                  {/*</NavLink>*/}
                                                                              </ListItem>
                                                                          ))}
                                                                {nextToken && <Grid item ref={targetRef}></Grid>}
                                                            </List>
                                                        </Collapse>
                                                    )}
                                                </ListItem>
                                            </Tooltip>
                                        );
                                    })}
                            </List>
                        )}
                    </>
                )}

                {!isCms &&
                    activeStage &&
                    (compareVersions(activeStage.version, VERSION_CM_5112_PROJECT_SITE_ACCESS) < 0 ||
                        (activeStage.user &&
                            (activeStage.user.isSpaceAdmin ||
                                (activeProject &&
                                    activeStage.user.projects &&
                                    activeStage.user.projects.filter((p) => p.id === activeProject.id && p.isAdmin)
                                        .length > 0)))) && (
                        <Box sx={{ position: `relative`, minHeight: "64px", bottom: "0", backgroundColor: `#f2f3f6` }}>
                            <Divider sx={{ mb: 1.5, mx: 1.5 }} />
                            {menuType === "admin" ? (
                                <Tooltip
                                    title={navOpen ? "" : t(`common::label::${"Back to Project"}`, `Back to Project`)}
                                    placement={"right"}
                                >
                                    <BackToConsoleButton
                                        color="inherit"
                                        className={"back-to-console-button"}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            leave();
                                        }}
                                        startIcon={
                                            <Box width={22} height={22} borderRadius={5}>
                                                <IconBack size={12} htmlColor={`#fff`} />
                                            </Box>
                                        }
                                    >
                                        {t(`common::label::${"Back to Project"}`, `Back to Project`)}
                                    </BackToConsoleButton>
                                </Tooltip>
                            ) : (
                                <>
                                    <Tooltip title={navOpen ? "" : "Project Settings"} placement={"right"}>
                                        <ProjectSettingButton
                                            color="inherit"
                                            className={"project-setting-button"}
                                            onClick={() => {
                                                dispatch(closeModal());
                                                project.id
                                                    ? history.push(
                                                          menuService.paths.admin.projectSettings(spaceId, projectId),
                                                      )
                                                    : "";
                                            }}
                                            startIcon={
                                                <IconSettingProject
                                                    size={16}
                                                    htmlColor={theme.palette.greySecondary.main}
                                                />
                                            }
                                            endIcon={
                                                <Box borderRadius={5}>
                                                    <IconArrowCircleRight size={22} />
                                                </Box>
                                            }
                                        >
                                            <span className={"nav-button-label"}>
                                                {t(`common::label::${"Project Settings"}`)}
                                            </span>
                                        </ProjectSettingButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    )}
                <Tooltip title={navOpen ? "Folding" : "Open"} placement={"right"} leaveDelay={-200}>
                    <IconButton
                        className={"nav-toggle-button"}
                        color={navOpen ? "primary" : "inherit"}
                        variant={"contained"}
                        circled
                        onClick={handleNavToggle}
                        edge="start"
                    >
                        {getNavToggleIcon()}
                    </IconButton>
                </Tooltip>
            </Drawer>
        </>
    );
};

export default Navigator;
